import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { SyncActivityEvent } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';
import { getOtaLabel } from '../OtaLogo';
import { syncDirectionLabel, syncEventLabel, syncStatusLabel } from './syncActivityLabels';

interface Props {
  event: SyncActivityEvent;
  onOpenDetails?: (event: SyncActivityEvent) => void;
}

const DOT_CLASS: Record<string, string> = {
  processed: 'bg-sage',
  failed: 'bg-sealwax',
  parked: 'bg-amber',
  received: 'bg-ink-soft',
  pending: 'bg-ink-soft',
};

export function SyncActivityRow({ event, onOpenDetails }: Props) {
  const isFailed = event.status === 'failed';
  const isParked = event.status === 'parked';
  const isPending = event.status === 'received' || event.status === 'pending';

  const body = (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2.5">
      <span
        aria-hidden
        className={cn(
          'mt-1.5 inline-block size-2 shrink-0 rounded-full',
          DOT_CLASS[event.status] ?? 'bg-ink-faint',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
          <span className="text-ink-faint text-[10px] uppercase tracking-wider">
            {syncDirectionLabel(event.source)}
          </span>
          <span className="text-ink wrap-break-word">{syncEventLabel(event.eventType)}</span>
          {/* Farbe erklärt nie allein einen Zustand (§11) — der Punkt braucht sein Wort. */}
          <span
            className={cn(
              'text-xs',
              isFailed ? 'text-sealwax font-medium' : isParked ? 'text-amber' : 'text-ink-soft',
            )}
          >
            · {syncStatusLabel(event.status)}
          </span>
          {event.otaName && (
            <span className="text-ink-soft text-xs">· {getOtaLabel(event.otaName)}</span>
          )}
        </div>
        {event.error && (
          <p
            className={cn(
              'wrap-break-word mt-1 text-xs',
              isFailed ? 'text-sealwax' : isParked ? 'text-amber' : 'text-ink-soft',
            )}
          >
            {event.error.length > 200 ? `${event.error.slice(0, 200)}…` : event.error}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <span className="text-ink-soft text-xs">{formatRelative(event.occurredAt)}</span>
        {onOpenDetails && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onOpenDetails(event);
            }}
            // 44 px nach §9; die negativen Ränder halten die Zeilenhöhe unverändert. Vorher
            // war das ein 20-px-Ziel und der einzige Weg zum Inhalt des Vorgangs.
            className="text-ink-soft hover:text-ink -my-3 -mr-2 flex size-11 shrink-0 items-center justify-center rounded transition-colors"
            title={`${syncEventLabel(event.eventType)} — Einzelheiten anzeigen`}
            aria-label={`${syncEventLabel(event.eventType)} — Einzelheiten anzeigen`}
          >
            <Search size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );

  // Der Weg in die Buchung steht nur, wenn es sie wirklich gibt — ein gescheiterter oder
  // zurückgestellter Vorgang hat noch keine.
  if (event.domainBookingId && !isFailed && !isParked && !isPending) {
    return (
      <li>
        <Link
          to="/bookings/$id"
          params={{ id: event.domainBookingId }}
          className="hover:bg-card-alt/50 -mx-3 block rounded px-3 transition-colors"
        >
          {body}
        </Link>
      </li>
    );
  }
  return <li>{body}</li>;
}
